import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { ChevronLeft, Target, Copy, CheckCircle2, QrCode, Share2, Coins, Crown } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import FpStreakCard from '../../components/FpStreakCard';
import AnimatedGiftBox from './AnimatedGiftBox';

export default function RewardsTab({ user, wallet, copyReferral, goBack, refreshWallet, initialSubTab }) {
  const [subTab, setSubTab] = useState('tasks');
  const [tasks, setTasks] = useState([]);
  const [history, setHistory] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [myTier, setMyTier] = useState(null);
  // Toast-like celebration row shown inline under the claimed task so the
  // player can SEE their balance jump without waiting for the wallet tab.
  const [lastClaim, setLastClaim] = useState(null);
  useEffect(() => {
    if (initialSubTab?.tab) {
      const allowed = ['tasks', 'cashback'];
      setSubTab(allowed.includes(initialSubTab.tab) ? initialSubTab.tab : 'tasks');
    }
  }, [initialSubTab?.nonce, initialSubTab?.tab]);

  useEffect(() => { fetchTasks(); fetchMyTier(); }, []);

  const fetchMyTier = async () => {
    try {
      const res = await api.get(`/api/tiers/my-tier?_=${Date.now()}`, {
        headers: { 'Cache-Control': 'no-cache, no-store' },
      });
      setMyTier(res.data);
    } catch {}
  };

  const fetchTasks = async () => {
    try {
      const bust = `_=${Date.now()}`;
      const noStore = { headers: { 'Cache-Control': 'no-cache, no-store' } };
      const [t, h] = await Promise.all([
        api.get(`/api/tasks?${bust}`, noStore),
        api.get(`/api/tasks/history?${bust}`, noStore),
      ]);
      setTasks(t.data.tasks || []);
      setHistory(h.data.claims || []);
    } catch {} finally { setTasksLoading(false); }
  };

  const handleClaim = async (taskId) => {
    setClaiming(taskId);
    const claimedTask = tasks.find((t) => t.id === taskId);
    try {
      const res = await api.post(`/api/tasks/${taskId}/claim`);
      const rewardType = (res.data.reward_type || claimedTask?.reward_type || 'fp').toLowerCase();
      const unit = rewardType === 'cash' ? '$' : 'FP';
      const amt = res.data.reward_sc ?? 0;
      toast.success(res.data.message || (rewardType === 'cash' ? `+$${amt} added!` : `+${amt} FP added!`));
      // Eager updates so the UI feels live — no waiting for next poll cycle.
      setLastClaim({
        task_id: taskId,
        task_title: claimedTask?.title || 'Reward',
        reward_sc: res.data.reward_sc || claimedTask?.reward_sc || 0,
        reward_type: rewardType,
        unit,
        new_balance: res.data.new_balance ?? res.data.wallet_balance ?? null,
        claimed_at: new Date().toISOString(),
      });
      // Refresh reward-related lists + global wallet + tier status.
      await Promise.all([fetchTasks(), fetchMyTier()]);
      if (refreshWallet) await refreshWallet();
      // Auto-clear the celebration banner after 4s so it doesn't linger.
      setTimeout(() => setLastClaim((c) => (c && c.task_id === taskId ? null : c)), 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Claim failed');
    } finally {
      setClaiming(null);
    }
  };

  const taskIcon = (type) => ({ daily_login: '📅', weekly_login: '📆', total_deposit: '💰', deposit_count: '💳', total_spend: '⚡', weekly_spend: '🔥', total_win: '🏆', play_games: '🎮', refer_friends: '👥', first_deposit: '⭐' }[type] || '🎯');

  const referralCount = myTier?.referral_count || 0;
  const isCashbackTask = (task) => /cashback/i.test(`${task?.title || ''} ${task?.description || ''} ${task?.task_type || ''} ${task?.criteria?.label || ''}`);
  const cashbackTasks = tasks.filter(isCashbackTask);
  const missionTasks = tasks.filter((task) => !isCashbackTask(task));

  return (
    <div className="vl-pirate-page vl-rewards-page space-y-5 pb-28 sm:pb-10" style={{ animation: 'fadeInUp 0.4s ease' }} data-testid="rewards-tab">
      <header className="vl-rewards-page__masthead"><span><Crown /></span><div><p>Crew Spoils</p><h1>Treasure Rewards</h1></div><i className="vl-page-masthead__lantern" aria-hidden="true" /></header>
      <button onClick={goBack} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition" data-testid="rewards-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      {lastClaim && (
        <div
          className="rounded-xl px-3 py-2 text-right"
          style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.35)', animation: 'fadeInUp 0.3s ease' }}
          data-testid="last-claim-banner"
        >
          <p className="text-[10px] uppercase tracking-wider text-green-400">Just claimed</p>
          <p className="text-sm font-bold text-green-400">+{lastClaim.reward_sc} FP</p>
          <p className="text-[10px] text-gray-300 truncate">{lastClaim.task_title}</p>
        </div>
      )}

      {/* Sub-tabs */}
      <div
        className="grid grid-cols-2 rounded-[22px] p-1.5 gap-1.5"
        style={{ background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(245,215,110,0.12)' }}
        data-testid="rewards-subtab-scroller"
      >
        {[{ id: 'tasks', label: 'Missions', icon: Target }, { id: 'cashback', label: 'Cashback', icon: Coins }].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl text-sm font-black transition-all whitespace-nowrap ${subTab === t.id ? 'shadow-[0_10px_28px_rgba(212,175,55,0.16)]' : 'text-gray-500 hover:text-gray-300'}`}
            style={subTab === t.id ? { background: 'linear-gradient(135deg, rgba(212,175,55,0.26), rgba(212,175,55,0.07))', color: '#F5D76E', border: '1px solid rgba(245,215,110,0.22)' } : {}}
            data-testid={`rewards-subtab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Daily FP streak drip — lives inside the Missions tab so players
          see it alongside their other task claims rather than on Home. */}
      {subTab === 'tasks' && (
        <FpStreakCard
          onClaimed={async (claim) => {
            setLastClaim({
              task_id: 'fp-streak',
              task_title: `Daily FP Streak · Day ${claim?.day || ''}`.trim(),
              reward_sc: claim?.reward_fp || 0,
              reward_type: 'fp',
              unit: 'FP',
              new_balance: claim?.new_fp_balance ?? null,
              claimed_at: new Date().toISOString(),
            });
            await Promise.all([fetchTasks(), fetchMyTier()]);
            await refreshWallet?.();
            setTimeout(() => setLastClaim((c) => (c && c.task_id === 'fp-streak' ? null : c)), 4000);
          }}
        />
      )}

      {/* ===== CASHBACK ===== */}
      {subTab === 'cashback' && (
        <div className="vl-cashback-treasure space-y-4" data-testid="player-cashback-section">
          <section className="vl-cashback-treasure__hero">
            <div className="vl-cashback-treasure__coin"><Coins /></div>
            <p>Captain's Cashback</p>
            <h2>Losses can return as treasure</h2>
            <span>Complete an active cashback voyage, then claim the FP reward here.</span>
          </section>
          {tasksLoading ? <div className="flex justify-center py-8"><div className="spinner" /></div> : cashbackTasks.length === 0 ? (
            <section className="vl-cashback-treasure__empty">
              <Coins className="w-9 h-9" /><h3>No cashback voyage active</h3><p>The captain will post the next cashback drop here.</p>
            </section>
          ) : cashbackTasks.map((task) => (
            <section key={task.id} className="vl-cashback-task" data-testid={`cashback-task-${task.id}`}>
              <div><span>{taskIcon(task.task_type)}</span><div><h3>{task.title}</h3><p>{task.description}</p></div></div>
              <div className="vl-cashback-task__progress"><i style={{ width: `${task.progress_pct || 0}%` }} /></div>
              <footer><small>{task.current_value || 0} / {task.target_value || 0}</small><strong>+{task.reward_sc || 0} FP</strong>
                {task.can_claim && <button onClick={() => handleClaim(task.id)} disabled={claiming === task.id} data-testid={`claim-cashback-${task.id}`}>{claiming === task.id ? 'Claiming…' : 'Claim Treasure'}</button>}
              </footer>
            </section>
          ))}
        </div>
      )}

      {/* ===== MISSIONS ===== */}
      {subTab === 'tasks' && (
        <div className="space-y-3">
          {tasksLoading ? <div className="flex justify-center py-8"><div className="spinner" /></div> : missionTasks.length === 0 ? (
            <div className="text-center py-12 text-gray-500"><Target className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>No active tasks. Check back later!</p></div>
          ) : missionTasks.map(task => (
            <div key={task.id} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid={`player-task-${task.id}`}>
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">{taskIcon(task.task_type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-sm text-white">{task.title}</h3>
                    <span className="text-sm font-bold" style={{ color: '#A5B4FC' }}>{task.reward_sc} FP</span>
                  </div>
                  {task.description && <p className="text-xs text-gray-400 mb-2">{task.description}</p>}
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                      <span>{task.current_value} / {task.target_value}</span>
                      <span>{task.progress_pct}%</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${task.progress_pct}%`, background: task.progress_pct >= 100 ? '#22c55e' : 'linear-gradient(90deg, #D4AF37, #F5D76E)' }} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1">
                      {task.is_recurring && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400">{task.period?.toUpperCase()}</span>}
                      {task.total_claims > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400">Claimed {task.total_claims}x</span>}
                    </div>
                    {task.can_claim ? (
                      <button onClick={() => handleClaim(task.id)} disabled={claiming === task.id} data-testid={`claim-task-${task.id}`}
                        className="px-4 py-1.5 rounded-lg text-xs font-bold text-white transition hover:scale-105" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}>
                        {claiming === task.id ? '...' : 'Claim'}
                      </button>
                    ) : task.claimed_this_period ? (
                      <span className="text-[10px] text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Done</span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {history.length > 0 && (
            <div className="pt-3">
              <p className="text-xs text-gray-500 mb-2">Recent Claims</p>
              {history.slice(0, 5).map(c => (
                <div key={c.id} className="flex items-center justify-between py-2 px-3 rounded-lg mb-1" style={{ background: 'rgba(255,255,255,0.02)' }}>
                  <div><p className="text-xs font-medium text-white">{c.task_title}</p><p className="text-[10px] text-gray-500">{new Date(c.claimed_at).toLocaleDateString()}</p></div>
                  <span className="text-xs font-bold text-green-400">+{c.reward_sc} FP</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * Referral panel — hero card with first-deposit referral rewards, the player's code + copy
 * button, a branded QR code of their deep-linked signup URL (scan with
 * a friend's phone camera → lands on signup with ref pre-filled), and
 * a native share sheet button (Web Share API where available).
 *
 * The link encodes the referral code via `?ref=<code>` — the AuthPage
 * already reads this query param on mount and pre-fills the referral
 * input.
 */
function LifetimeEarningsCard({ referralCount = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/api/referrals/my-lre-earnings');
        setData(r.data);
      } catch { /* swallow — banner stays hidden */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;
  if (!data) return null;

  const lt = data.lifetime || {};
  const tm = data.thisMonth || data.this_month || {};
  const totalEarnedLabel = lt.cash > 0
    ? `$${Number(lt.cash || 0).toFixed(2)}`
    : `${Number(lt.fp || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} FP`;

  return (
    <div
      className="rounded-[24px] p-5 sm:p-6 space-y-4 anim-fadeInUp"
      style={{
        background: 'radial-gradient(circle at 12% 18%, rgba(34,197,94,0.20), transparent 34%), linear-gradient(135deg, rgba(34,197,94,0.09), rgba(212,175,55,0.04))',
        border: '1px solid rgba(34,197,94,0.30)',
      }}
      data-testid="lre-earnings-card"
    >
      <div className="flex items-center gap-2">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.18)', border: '1px solid rgba(34,197,94,0.40)' }}>
          <AnimatedGiftBox size="xs" />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-lg font-black text-white">Referral Earnings</h4>
          <p className="text-xs text-gray-400">Earn real cash from referral first deposits.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl p-3.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Referrals</p>
          <p className="text-2xl font-black text-[#F5D76E] tabular-nums mt-0.5">{Number(referralCount || 0).toLocaleString()}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{data.count_active_referees || 0} active earners</p>
        </div>
        <div className="rounded-xl p-3.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Lifetime Earned</p>
          <p className="text-2xl font-black text-indigo-300 tabular-nums mt-0.5">{totalEarnedLabel}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{lt.count || 0} payouts</p>
        </div>
        <div className="rounded-xl p-3.5 col-span-2 sm:col-span-1" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">This Month</p>
          <div className="flex items-baseline gap-1.5 mt-0.5">
            {tm.cash > 0 && <span className="text-xl font-black text-emerald-400 tabular-nums">${tm.cash.toFixed(2)}</span>}
            {tm.fp > 0 && <span className="text-xl font-black text-indigo-300 tabular-nums">{tm.fp.toFixed(2)} FP</span>}
            {(tm.cash || 0) + (tm.fp || 0) === 0 && <span className="text-xl font-black text-gray-500">—</span>}
          </div>
          <p className="text-[10px] text-gray-500 mt-0.5">{tm.count || 0} payouts</p>
        </div>
      </div>

      {data.per_referee && data.per_referee.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">
            Top earners ({data.count_active_referees})
          </p>
          <div className="space-y-1" data-testid="lre-per-referee-list">
            {data.per_referee.slice(0, 3).map(r => (
              <div key={r.referee_id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <span className="text-white truncate font-medium">{r.username}</span>
                <span className="text-gray-400 text-[10px] ml-2 flex-shrink-0">
                  {r.deposits} deposits · ${r.deposit_total.toFixed(2)}
                </span>
                <span className="text-emerald-400 font-bold tabular-nums ml-2 flex-shrink-0">
                  +{r.earned_total.toFixed(2)} {r.currencies?.includes('cash') ? '$' : 'FP'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ReferralPanel({ user, referralCount, copyReferral }) {
  const [rules, setRules] = useState({
    player_referral_enabled: true,
    referrer_first_deposit_commission_percent: 33,
    referrer_reward_currency: 'cash',
    referrer_earning_deposit_count: 1,
    referee_first_deposit_bonus_percent: 100,
    referee_first_deposit_bonus_currency: 'fp',
  });

  useEffect(() => {
    // Pull the live referral bonus values so the UI numbers match what
    // the backend actually credits on a qualifying signup.
    (async () => {
      try {
        const res = await api.get('/api/referrals/rules');
        if (res.data) setRules(res.data);
      } catch { /* keep defaults */ }
    })();
  }, []);

  const code = user?.referral_code || '';
  // Deep link: origin + /auth + ?ref=CODE&src=... so friends land on signup
  // with the referral + source channel baked in. Source = {qr|share|copy}
  // lets the admin referral analytics show which channel drove each signup.
  const buildLink = (src) => code
    ? `${window.location.origin}/auth?ref=${encodeURIComponent(code)}&src=${src}`
    : '';
  const qrLink = buildLink('qr');
  const shareLink = buildLink('share');
  const copyFallbackLink = buildLink('copy');

  const copyLink = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(copyFallbackLink);
      toast.success('Referral link copied — send it to a friend!');
    } catch {
      toast.error('Could not copy — long-press the QR or link below');
    }
  };

  const nativeShare = async () => {
    if (!code) return;
    const data = {
      title: 'Join me on Vegas Lobby',
      text: `Sign up with my code ${code}. You get a 100% first deposit bonus in FP, and I earn 33% of your first deposit.`,
      url: shareLink,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await copyLink();
      }
    } catch {
      /* user cancelled — no-op */
    }
  };

  return (
    <div className="space-y-4" data-testid="referral-panel">
      <div
        className="rounded-[28px] p-6 sm:p-8 text-center"
        style={{ background: 'radial-gradient(circle at 50% 0%, rgba(34,197,94,0.26), transparent 36%), linear-gradient(135deg, #0a2e1a, #0d1117)', border: '1px solid rgba(34,197,94,0.24)' }}
      >
        <AnimatedGiftBox size="lg" className="mx-auto mb-4" />
        <h3 className="text-3xl sm:text-4xl font-black text-white mb-2">Refer & Earn</h3>
        <p className="text-base text-gray-300 mb-2 leading-7">
          {rules.player_referral_enabled === false
            ? 'Player referral bonuses are currently paused.'
            : 'Earn real cash from your referral first deposit.'}
        </p>
        <p className="text-sm text-[#F5D76E] font-bold mb-5">Your Referrals: {referralCount}</p>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-2xl p-4" style={{ background: 'rgba(34,197,94,0.12)' }}>
            <p className="text-3xl font-black text-green-400" data-testid="referrer-bonus-fp">{Number(rules.referrer_first_deposit_commission_percent || 33).toLocaleString()}%</p>
            <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider">You earn in cash</p>
          </div>
          <div className="rounded-2xl p-4" style={{ background: 'rgba(59,130,246,0.12)' }}>
            <p className="text-3xl font-black text-blue-400" data-testid="referee-bonus-fp">{Number(rules.referee_first_deposit_bonus_percent || 100).toLocaleString()}%</p>
            <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider">Friend bonus in FP</p>
          </div>
        </div>
        <p className="text-[12px] text-gray-300 leading-relaxed mb-5">
          You earn {Number(rules.referrer_first_deposit_commission_percent || 33).toLocaleString()}% of your referral's first deposit in your main balance. Your referral gets a {Number(rules.referee_first_deposit_bonus_percent || 100).toLocaleString()}% first deposit bonus in FP.
        </p>

        {/* Code + copy */}
        <div className="flex gap-2 mb-3">
          <div
            className="flex-1 rounded-xl px-4 py-2.5 font-mono text-sm text-white flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            data-testid="referral-code-display"
          >
            {code || 'N/A'}
          </div>
          <button
            onClick={copyReferral}
            className="px-5 rounded-xl font-semibold text-sm text-black"
            style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
            data-testid="copy-referral-btn"
            aria-label="Copy referral code"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>

        {/* Native share — primary CTA on mobile; falls back to copy link */}
        {code && (
          <button
            onClick={nativeShare}
            className="w-full rounded-xl py-3 font-bold text-sm text-black transition active:scale-[0.98] flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D76E)' }}
            data-testid="referral-share-btn"
          >
            <Share2 className="w-4 h-4" /> Share Invite Link
          </button>
        )}
      </div>

      {/* QR card — the crown-jewel in-person conversion tool. Friend
          points their phone camera at the QR → lands on /auth?ref=CODE
          with the code already baked in. No typing, no messaging apps
          required. */}
      {code && (
        <div
          className="rounded-2xl p-5"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
          data-testid="referral-qr-card"
        >
          <div className="flex items-center gap-2 mb-3">
            <QrCode className="w-4 h-4 text-[#D4AF37]" />
            <h4 className="text-sm font-semibold text-white">Scan to Join</h4>
            <span className="ml-auto text-[10px] text-gray-500">In-person referrals</span>
          </div>
          <div className="flex gap-3 items-center">
            <div className="bg-white rounded-xl p-2.5 shrink-0">
              <QRCodeSVG value={qrLink} size={112} level="M" data-testid="referral-qr" />
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <p className="text-[12px] text-gray-300 leading-relaxed">
                Have a friend point their phone camera at this QR. They land on signup with your code <b className="text-[#F5D76E]">{code}</b> already filled in.
              </p>
              <button
                onClick={copyLink}
                className="w-full rounded-lg py-2 text-[11px] font-medium text-[#D4AF37] hover:bg-[#D4AF37]/10 transition"
                style={{ border: '1px solid rgba(212,175,55,0.3)' }}
                data-testid="copy-referral-link-btn"
              >
                <Copy className="w-3 h-3 inline mr-1" /> Copy invite link
              </button>
            </div>
          </div>
          <p className="text-[10px] text-gray-600 mt-3 break-all font-mono">
            {qrLink}
          </p>
        </div>
      )}
      <LifetimeEarningsCard referralCount={referralCount} />
    </div>
  );
}
